
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue'
import Breadcrumbs from '../../../components/Breadcrumbs.vue'
import { TASK_LIBRARY, TaskLibraryItem } from '../data/taskLibrary'

const COLUMNS: { key: keyof TaskLibraryItem | 'num'; title: string; width: number }[] = [
    { key: 'area', title: 'Функциональная область', width: 120 },
    { key: 'num', title: '№', width: 44 },
    { key: 'goal', title: 'Цель', width: 220 },
    { key: 'task', title: 'Задача', width: 280 },
    { key: 'formulation', title: 'Формулировка задачи', width: 180 },
    { key: 'explanation', title: 'Пояснения', width: 200 },
    { key: 'comments', title: 'Комментарии', width: 320 },
]

const HEADER_BG = '#fff3bf'
const GRID = '#c5c8ce'
const TEXT = '#2a3b51'
const ROW_BG = '#ffffff'
const ROW_ALT = '#fafafa'
const PADDING = 8
const FONT = '13px "Source Sans Pro", Arial, sans-serif'
const HEADER_FONT = 'bold 13px "Source Sans Pro", Arial, sans-serif'
const LINE_HEIGHT = 18
const MIN_TABLE_WIDTH = 1400

function formatArea(area: string): string {
    if (area === 'HR') return 'HR'
    return area.charAt(0).toUpperCase() + area.slice(1)
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const normalized = String(text || '')
        .replace(/\r/g, '')
        .split('\n')
        .flatMap(line => {
            const words = line.split(/\s+/).filter(Boolean)
            if (!words.length) return ['']
            const lines: string[] = []
            let current = words[0]
            for (let i = 1; i < words.length; i++) {
                const test = `${current} ${words[i]}`
                if (ctx.measureText(test).width <= maxWidth) {
                    current = test
                } else {
                    lines.push(current)
                    current = words[i]
                }
            }
            lines.push(current)
            return lines
        })
    return normalized.length ? normalized : ['']
}

function cellValue(item: TaskLibraryItem, key: keyof TaskLibraryItem | 'num'): string {
    if (key === 'area') return formatArea(item.area)
    if (key === 'num') return String(item.num)
    return String(item[key] || '')
}

export default defineComponent({
    name: 'TaskLibrary',
    components: {
        Breadcrumbs,
    },
    setup() {
        const instance = getCurrentInstance()
        const canvasRef = ref<HTMLCanvasElement | null>(null)
        const containerRef = ref<HTMLElement | null>(null)
        const baseTableWidth = COLUMNS.reduce((sum, col) => sum + col.width, 0)
        const tableWidth = ref(baseTableWidth)

        const getScaledColumns = (width: number) => {
            const scale = width / baseTableWidth
            const scaled = COLUMNS.map(col => ({
                ...col,
                width: Math.floor(col.width * scale),
            }))
            const used = scaled.reduce((sum, col) => sum + col.width, 0)
            const last = scaled[scaled.length - 1]
            last.width += width - used
            return scaled
        }

        const projectId = computed(() => {
            return instance?.proxy?.$router?.currentRoute?.params?.id || ''
        })

        const project = computed(() => {
            if (!projectId.value || !instance?.proxy?.$store) return {}
            return instance.proxy.$store.getters.projectById(projectId.value) || {}
        })

        const breadcrumbs = computed(() => [
            {
                text: 'Beauty Автопилот',
                to: `/project/${projectId.value}/GCB2/BeautyAutopilot`,
            },
            {
                text: 'Библиотека задач',
                to: '',
                disabled: true,
            },
        ])

        const measureRowHeight = (
            ctx: CanvasRenderingContext2D,
            item: TaskLibraryItem,
            columns: { key: keyof TaskLibraryItem | 'num'; width: number }[]
        ): number => {
            let maxLines = 1
            columns.forEach(col => {
                ctx.font = FONT
                const lines = wrapText(ctx, cellValue(item, col.key), col.width - PADDING * 2)
                maxLines = Math.max(maxLines, lines.length)
            })
            return Math.max(40, maxLines * LINE_HEIGHT + PADDING * 2)
        }

        const drawTable = () => {
            const canvas = canvasRef.value
            const container = containerRef.value
            if (!canvas || !container) return
            const ctx = canvas.getContext('2d')
            if (!ctx) return

            // Растягиваем на ширину контейнера, но не уже MIN_TABLE_WIDTH.
            // Уже 1400px — горизонтальный скролл.
            const containerWidth = Math.floor(container.clientWidth)
            const availableWidth = Math.max(containerWidth, MIN_TABLE_WIDTH)
            tableWidth.value = availableWidth
            const columns = getScaledColumns(availableWidth)

            const headerHeight = 44
            const rowHeights = TASK_LIBRARY.map(item => measureRowHeight(ctx, item, columns))
            const totalHeight = headerHeight + rowHeights.reduce((a, b) => a + b, 0)
            const dpr = window.devicePixelRatio || 1

            canvas.width = Math.floor(availableWidth * dpr)
            canvas.height = Math.floor(totalHeight * dpr)
            canvas.style.width = `${availableWidth}px`
            canvas.style.height = `${totalHeight}px`
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

            ctx.fillStyle = HEADER_BG
            ctx.fillRect(0, 0, availableWidth, headerHeight)

            let x = 0
            columns.forEach((col, colIndex) => {
                ctx.strokeStyle = GRID
                ctx.strokeRect(x, 0, col.width, headerHeight)
                ctx.fillStyle = TEXT
                ctx.font = HEADER_FONT
                const lines = wrapText(ctx, COLUMNS[colIndex].title, col.width - PADDING * 2)
                lines.forEach((line, i) => {
                    ctx.fillText(line, x + PADDING, PADDING + LINE_HEIGHT * (i + 0.75))
                })
                x += col.width
            })

            let y = headerHeight
            TASK_LIBRARY.forEach((item, index) => {
                const h = rowHeights[index]
                ctx.fillStyle = index % 2 === 0 ? ROW_BG : ROW_ALT
                ctx.fillRect(0, y, availableWidth, h)

                x = 0
                columns.forEach(col => {
                    ctx.strokeStyle = GRID
                    ctx.strokeRect(x, y, col.width, h)
                    ctx.fillStyle = TEXT
                    ctx.font = FONT
                    const lines = wrapText(ctx, cellValue(item, col.key), col.width - PADDING * 2)
                    lines.forEach((line, i) => {
                        ctx.fillText(line, x + PADDING, y + PADDING + LINE_HEIGHT * (i + 0.75))
                    })
                    x += col.width
                })
                y += h
            })

            ctx.save()
            ctx.globalAlpha = 0.06
            ctx.fillStyle = TEXT
            ctx.font = 'bold 48px "Source Sans Pro", Arial, sans-serif'
            ctx.translate(availableWidth / 2, totalHeight / 2)
            ctx.rotate(-Math.PI / 6)
            for (let i = -3; i <= 3; i++) {
                ctx.fillText('Beauty Автопилот', -180, i * 120)
            }
            ctx.restore()
        }

        const blockEvent = (e: Event) => {
            e.preventDefault()
            e.stopPropagation()
            return false
        }

        const onKeyDown = (e: KeyboardEvent) => {
            const key = e.key.toLowerCase()
            const blocked =
                (e.ctrlKey || e.metaKey) && ['c', 'x', 'a', 's', 'p', 'u'].includes(key)
            if (blocked || key === 'f12' || (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(key))) {
                e.preventDefault()
                e.stopPropagation()
            }
        }

        const onBeforePrint = () => {
            document.body.classList.add('task-library-print-blocked')
        }

        const onAfterPrint = () => {
            document.body.classList.remove('task-library-print-blocked')
        }

        let resizeObserver: ResizeObserver | null = null

        onMounted(async () => {
            await nextTick()
            drawTable()
            requestAnimationFrame(drawTable)

            const container = containerRef.value
            if (container && typeof ResizeObserver !== 'undefined') {
                resizeObserver = new ResizeObserver(() => drawTable())
                resizeObserver.observe(container)
            } else {
                window.addEventListener('resize', drawTable)
            }

            document.addEventListener('keydown', onKeyDown, true)
            document.addEventListener('copy', blockEvent, true)
            document.addEventListener('cut', blockEvent, true)
            document.addEventListener('contextmenu', blockEvent, true)
            document.addEventListener('dragstart', blockEvent, true)
            document.addEventListener('selectstart', blockEvent, true)
            window.addEventListener('beforeprint', onBeforePrint)
            window.addEventListener('afterprint', onAfterPrint)
        })

        onBeforeUnmount(() => {
            if (resizeObserver) {
                resizeObserver.disconnect()
                resizeObserver = null
            } else {
                window.removeEventListener('resize', drawTable)
            }
            document.removeEventListener('keydown', onKeyDown, true)
            document.removeEventListener('copy', blockEvent, true)
            document.removeEventListener('cut', blockEvent, true)
            document.removeEventListener('contextmenu', blockEvent, true)
            document.removeEventListener('dragstart', blockEvent, true)
            document.removeEventListener('selectstart', blockEvent, true)
            window.removeEventListener('beforeprint', onBeforePrint)
            window.removeEventListener('afterprint', onAfterPrint)
            document.body.classList.remove('task-library-print-blocked')
        })

        return {
            canvasRef,
            containerRef,
            tableWidth,
            breadcrumbs,
        }
    },
})
